import React, { useContext, useEffect, useRef, useState } from "react";
import { CLOUDINARY_CLOUD } from "../constants/CloudinaryConstants";
import { HOST } from "../constants/HostConstants";
import { AlertContext } from "../context/MainContext";
import { FaTimes, FaCloudUploadAlt, FaCheck } from "react-icons/fa";

// Fixed ad sizes — only two formats
const AD_FORMATS = {
  horizontal: {
    label: "Horizontal Banner",
    width: 728,
    height: 90,
    description: "Displayed across the top of pages",
  },
  vertical: {
    label: "Vertical Banner",
    width: 300,
    height: 600,
    description: "Displayed in the sidebar on property pages",
  },
};

const AdminAdCreate = ({ onSuccess, onClose }) => {
  const cloudinaryRef = useRef();
  const widgetRef = useRef();
  const { setAlert } = useContext(AlertContext);

  // Form state
  const [adData, setAdData] = useState({
    advertisement_url: "",
    link_url: "",
    alignment: "horizontal",
    advertisement_type: "image",
  });

  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cloudinaryReady, setCloudinaryReady] = useState(false);

  // Initialize Cloudinary
  useEffect(() => {
    const initCloudinary = async () => {
      try {
        cloudinaryRef.current = window.cloudinary;
        if (!cloudinaryRef.current) {
          console.error("Cloudinary not available");
          return;
        }

        const res = await fetch(`${HOST}/api/user/cloudinarySign`);
        if (!res.ok) throw new Error("Sign failed");

        const sign = await res.json();
        const opts = {
          cloudName: sign.cloud || CLOUDINARY_CLOUD,
          uploadSignature: sign.signature,
          uploadSignatureTimestamp: sign.timestamp,
          apiKey: sign.api_key,
          maxImageWidth: 2000,
          maxImageHeight: 2000,
        };

        if (sign.preset) opts.uploadPreset = sign.preset;

        widgetRef.current = cloudinaryRef.current.createUploadWidget(
          opts,
          function (error, result) {
            if (error) {
              setAlert([
                "Upload Error",
                "Failed to upload image: " + error.message,
                "failed",
              ]);
              setUploading(false);
              return;
            }

            if (result && result["event"] === "success") {
              const imageUrl = result["info"]["secure_url"];
              setUploadedImageUrl(imageUrl);
              setAdData((prev) => ({
                ...prev,
                advertisement_url: imageUrl,
              }));
              setAlert([
                "Image Uploaded",
                "Ad image uploaded successfully",
                "confirm",
              ]);
              setUploading(false);
            }

            if (result && result["event"] === "uploading") {
              setUploading(true);
            }
          },
        );

        setCloudinaryReady(true);
      } catch (err) {
        console.error("Cloudinary init error:", err);
        setAlert([
          "Upload Unavailable",
          "Could not initialize upload service",
          "failed",
        ]);
      }
    };

    initCloudinary();
  }, [setAlert]);

  const handleUploadClick = () => {
    if (!cloudinaryReady || !widgetRef.current) {
      setAlert(["Upload Unavailable", "Image upload is not ready", "failed"]);
      return;
    }
    widgetRef.current.open();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAdData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!adData.advertisement_url) {
      setAlert(["Missing Image", "Please upload an ad image", "failed"]);
      return;
    }

    if (!adData.link_url) {
      setAlert([
        "Missing URL",
        "Please enter the target URL for the ad",
        "failed",
      ]);
      return;
    }

    const format = AD_FORMATS[adData.alignment];

    setSubmitting(true);
    setAlert(["Creating Ad", "Please wait...", "waiting"]);

    try {
      const res = await fetch(`${HOST}/api/advertisement/adminCreate`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...adData,
          format_width: format.width,
          format_height: format.height,
        }),
      });

      if (!res.ok) throw new Error("Failed to create advertisement");

      const result = await res.json();

      if (result.success || result.advertisement_id) {
        setAlert([
          "Ad Created",
          "Advertisement created successfully and is now live site-wide",
          "confirm",
        ]);
        onSuccess?.();
        onClose?.();
      } else {
        throw new Error(result.error || "Unknown error");
      }
    } catch (error) {
      setAlert([
        "Creation Failed",
        error.message || "Failed to create advertisement",
        "failed",
      ]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-800">
            Create New Advertisement
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Image Upload Section */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Advertisement Image
            </label>
            {uploadedImageUrl ? (
              <div className="relative">
                <img
                  src={uploadedImageUrl}
                  alt="Advertisement Preview"
                  className="w-full h-auto max-h-48 object-cover rounded-lg border-2 border-green-500"
                />
                <button
                  type="button"
                  onClick={handleUploadClick}
                  className="absolute top-2 right-2 bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Change Image
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleUploadClick}
                disabled={!cloudinaryReady || uploading}
                className={`w-full py-4 border-2 border-dashed border-gray-300 rounded-lg text-center transition-all ${
                  cloudinaryReady && !uploading
                    ? "hover:border-blue-500 hover:bg-blue-50 cursor-pointer"
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                <FaCloudUploadAlt className="mx-auto mb-2 text-3xl text-gray-400" />
                <p className="text-gray-600 font-medium">
                  {uploading
                    ? "Uploading..."
                    : cloudinaryReady
                      ? "Click to upload advertisement image"
                      : "Loading upload service..."}
                </p>
              </button>
            )}
          </div>

          {/* Link URL */}
          <div>
            <label
              htmlFor="link_url"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Target URL
            </label>
            <input
              type="url"
              id="link_url"
              name="link_url"
              value={adData.link_url}
              onChange={handleInputChange}
              placeholder="https://example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Ad Format Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Ad Format
            </label>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(AD_FORMATS).map(([key, format]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() =>
                    setAdData((prev) => ({ ...prev, alignment: key }))
                  }
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    adData.alignment === key
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-200 hover:border-blue-300"
                  }`}
                >
                  <div className="font-semibold text-gray-800">
                    {format.label}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {format.width} × {format.height}px
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {format.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Size info */}
          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
            Selected:{" "}
            <span className="font-semibold">
              {AD_FORMATS[adData.alignment].label}
            </span>{" "}
            — {AD_FORMATS[adData.alignment].width} ×{" "}
            {AD_FORMATS[adData.alignment].height}px (fixed)
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !uploadedImageUrl}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <span className="animate-spin">⟳</span>
                  Creating...
                </>
              ) : (
                <>
                  <FaCheck />
                  Create Advertisement
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAdCreate;
